import { useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, IconButton, SectionLabel, TabPanel, Tabs } from '../../design/components'
import { EncounterTab } from './EncounterTab'
import { InitiativeRail } from './InitiativeRail'
import type { SessionControls } from './queries'
import type { LiveParticipant, LiveSession } from './types'
import styles from './live.module.css'

/** The tool groups, in the order an evening needs them. */
type ToolTab = 'cast' | 'selected' | 'order' | 'encounter' | 'session'

export interface GameMasterToolsProps {
  session: LiveSession
  controls: SessionControls
  selectedParticipantId: string | null
  onSelectParticipant: (participantId: string | null) => void
  /** Reordering the turn order. The strip drags; this is the keyboard-operable path. */
  onReorder: (from: number, to: number) => void
}

/**
 * Everything the Game Master presses, above the combat card.
 *
 * **Tabbed, and the tabs are the point.** Groups of tools stacked vertically pushed the combat
 * card — the thing actually being read during an exchange — below the fold on anything short of a
 * very tall screen. Tabbed, the tool block is a fixed height and the card starts where the eye
 * already is.
 *
 * The controls inside are **glyphs**, for the same reason: a row of worded buttons wraps to three
 * lines in a 380px column, and these are all things a Game Master presses often enough to learn.
 * Each carries its name for a screen reader and as a tooltip, so the icon is shorthand rather than
 * a riddle.
 *
 * **Cast is the first tab, where Place used to be**, and it absorbed it. Those were the same list
 * twice: "who is in this encounter" and "who is in this encounter and not yet on the map". Since a
 * session now opens on the cast rather than on a map, and since introducing somebody is the thing
 * a Game Master does most in the first half of an evening, the merged tab is the one that should
 * be open when the screen loads.
 *
 * Two things are deliberately *not* here: passing the turn, which lives with the initiative strip
 * beside the order it advances, and everything on the combat card, which is the section below.
 */
export function GameMasterTools({
  session,
  controls,
  selectedParticipantId,
  onSelectParticipant,
  onReorder,
}: GameMasterToolsProps) {
  const [tab, setTab] = useState<ToolTab>('cast')

  const live = session.status === 'Live'

  const selectedParticipant = session.participants.find(
    (participant) => participant.participantId === selectedParticipantId,
  )

  const selectedToken = session.tokens.find(
    (token) => token.participantId === selectedParticipantId,
  )

  const unknown = session.participants.filter((participant) => !participant.isKnown).length

  /**
   * How many copies of each character are in the session.
   *
   * Only used to decide whether a clone may be taken away: the last one cannot go from here,
   * because the encounter still says that character is in the fight and the next sync would put
   * it straight back.
   */
  const copies = new Map<number, number>()
  for (const participant of session.participants) {
    copies.set(participant.characterId, (copies.get(participant.characterId) ?? 0) + 1)
  }

  return (
    <Card variant="stunts" label="Tools">
      <Tabs
        label="Session tools"
        active={tab}
        onSelect={setTab}
        tabs={[
          {
            id: 'cast',
            label: 'Cast',
            // The count that matters here is who the table has *not* met — the number of
            // introductions still to make, which is the work this tab exists for.
            hint: unknown === 0 ? undefined : `${unknown} hidden`,
          },
          { id: 'selected', label: 'Token', hint: selectedParticipant?.name },
          { id: 'order', label: 'Order', hint: String(session.initiative.length) },
          { id: 'encounter', label: 'Encounter', hint: session.encounterType },
          { id: 'session', label: 'Session' },
        ]}
      />

      {tab === 'cast' && (
        <TabPanel id="cast">
          {session.participants.length === 0 ? (
            <p className={styles.note}>
              Nobody is in this encounter yet. Put people on a side from the Encounter tab and they
              appear here.
            </p>
          ) : (
            <>
              <p className={styles.note}>
                Player characters start introduced; everyone else does not. Introducing somebody
                puts their face on every screen — and, once the encounter has started, a line in the
                turn order.
              </p>

              <p className={styles.note}>
                A Temp character is a template: take as many copies of one as the room needs, and
                they arrive numbered. The copies live in this session only — ending it ends them,
                and the encounter goes on saying there is one.
              </p>

              {session.participants.map((participant) => (
                <CastRow
                  key={participant.participantId}
                  participant={participant}
                  session={session}
                  controls={controls}
                  live={live}
                  copies={copies.get(participant.characterId) ?? 1}
                  selected={selectedParticipantId === participant.participantId}
                  onSelect={() => onSelectParticipant(participant.participantId)}
                  onPlaced={() => {
                    onSelectParticipant(participant.participantId)
                    setTab('selected')
                  }}
                  onRemoved={() => {
                    // The selection cannot survive the thing it named, and leaving it pointing at
                    // a participant that no longer exists would leave the Token tab describing
                    // somebody who is gone.
                    if (selectedParticipantId === participant.participantId) {
                      onSelectParticipant(null)
                    }
                  }}
                />
              ))}
            </>
          )}
        </TabPanel>
      )}

      {tab === 'selected' && (
        <TabPanel id="selected">
          {selectedParticipant === undefined ? (
            <p className={styles.note}>
              Nobody selected. Pick a face off the map, the turn order, the legend or the cast.
            </p>
          ) : selectedToken === undefined ? (
            <p className={styles.note}>
              {selectedParticipant.name} is in the encounter but not on the map
              {session.mapImageUrl === null
                ? ' — there is no map to stand them on.'
                : '. The Cast tab is where somebody is placed.'}
            </p>
          ) : (
            <>
              <p className={styles.note}>
                {selectedParticipant.name} — drag on the map, or nudge with the arrow keys. Shift
                for a bigger step.
              </p>

              <div className={styles.glyphRow}>
                <IconButton
                  label={selectedToken.hidden ? 'Show where they are' : 'Hide where they are'}
                  icon={selectedToken.hidden ? 'show' : 'hide'}
                  onClick={() =>
                    controls.updateToken.mutate({
                      tokenId: selectedToken.tokenId,
                      hidden: !selectedToken.hidden,
                    })
                  }
                />

                {/* Out marks and does nothing else — they keep their place on the map and in the
                    order. Taking someone out of the order is a ruling, and it stays the GM's. */}
                <IconButton
                  label={
                    selectedParticipant.isDead
                      ? 'Back in the encounter'
                      : 'Mark out of the encounter'
                  }
                  icon={selectedParticipant.isDead ? 'refresh' : 'dead'}
                  onClick={() =>
                    controls.updateToken.mutate({
                      tokenId: selectedToken.tokenId,
                      isDead: !selectedParticipant.isDead,
                    })
                  }
                />

                {/* Off the map, not out of the encounter — the selection survives, because they
                    are still in the cast and still in the order. */}
                <IconButton
                  label="Take off the map"
                  icon="delete"
                  onClick={() => controls.removeToken.mutate({ tokenId: selectedToken.tokenId })}
                />
              </div>

              {selectedParticipant.isDead && (
                <p className={styles.note}>Marked out — still in the order.</p>
              )}

              <p className={styles.note}>
                Taking someone off the map does not take them out of the encounter: they stay in the
                cast and in the turn order. The Encounter tab is where somebody leaves entirely.
              </p>
            </>
          )}
        </TabPanel>
      )}

      {tab === 'order' && (
        <TabPanel id="order">
          {session.phase === 'PreEncounter' ? (
            <p className={styles.note}>
              There is no order before the encounter starts. Starting it puts everyone the table has
              met into one, in the order they are listed here.
            </p>
          ) : (
            /* The strip above drags; this reorders with buttons.
               Both exist on purpose — a drag is not operable from a keyboard, and `e2e` runs axe.
               It carries the rolled values too, which the strip has no room for. */
            <InitiativeRail
              entries={session.initiative}
              round={session.round}
              currentIndex={session.currentIndex}
              canControl={live}
              compact
              busy={controls.setInitiative.isPending}
              onAdvance={() => undefined}
              onReorder={onReorder}
            />
          )}
        </TabPanel>
      )}

      {tab === 'encounter' && (
        <TabPanel id="encounter">
          {live ? (
            <EncounterTab session={session} />
          ) : (
            <p className={styles.note}>
              The session is over. The encounter is still there and still editable — on its own
              screen.
            </p>
          )}
        </TabPanel>
      )}

      {tab === 'session' && (
        <TabPanel id="session">
          <SectionLabel tone="muted" micro>
            Watching
          </SectionLabel>
          {session.viewers.length === 0 ? (
            <p className={styles.note}>Nobody has the session open yet.</p>
          ) : (
            <p className={styles.note}>
              {session.viewers
                .map((viewer) => viewer.displayName)
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
            </p>
          )}

          {live && (
            <>
              <p className={styles.note}>
                Ending it clears everyone&apos;s screen. The encounter, the characters and the map
                are untouched — including every change made from the Encounter tab, which was
                written to the encounter itself. What does not survive is what was only ever this
                evening&apos;s: who had been introduced, and who was out of it.
              </p>
              <Button
                variant="danger"
                fullWidth
                disabled={controls.end.isPending}
                onClick={() => controls.end.mutate()}
              >
                End the session
              </Button>
            </>
          )}

          <Link to="/live">
            <Button variant="secondary" fullWidth>
              All live sessions
            </Button>
          </Link>
        </TabPanel>
      )}
    </Card>
  )
}

/**
 * One person in the encounter, and everything a Game Master does to them that is not on the map.
 *
 * Three controls, and they are three different questions. *Have they been introduced* is the
 * session's own state and the one this tab exists for. *Are they out of it* travels with the
 * person rather than with a token, which is why it is here as well as on the map. *Are they on
 * the map* only exists when there is a map — in a social encounter this row has two controls and
 * that is correct, not a degraded version of something.
 *
 * A `Temp` character gets two more, and they are a fourth question: *how many of these are there*.
 * They sit on the row rather than in a count field because a Game Master adds zombies as the
 * fiction produces them — one, and then two more when the door gives way — and a press per zombie
 * is the honest shape of that. Both are session-only: the encounter goes on saying there is one.
 */
function CastRow({
  participant,
  session,
  controls,
  live,
  copies,
  selected,
  onSelect,
  onPlaced,
  onRemoved,
}: {
  participant: LiveParticipant
  session: LiveSession
  controls: SessionControls
  live: boolean
  /** How many of this character are in the session. The last one cannot be taken away here. */
  copies: number
  selected: boolean
  onSelect: () => void
  onPlaced: () => void
  onRemoved: () => void
}) {
  const busy =
    controls.updateParticipant.isPending ||
    controls.addToken.isPending ||
    controls.addParticipant.isPending ||
    controls.removeParticipant.isPending

  return (
    <div className={`${styles.castRow} ${selected ? styles.castSelected : ''}`}>
      <button type="button" className={styles.castWho} aria-pressed={selected} onClick={onSelect}>
        <span className={styles.numbered}>
          {participant.portraitUrl != null ? (
            <img className={styles.trayThumb} src={participant.portraitUrl} alt="" loading="lazy" />
          ) : (
            <span className={styles.legendInitial} aria-hidden="true">
              {participant.name.slice(0, 1).toUpperCase()}
            </span>
          )}

          {participant.ordinal !== null && (
            <span className={styles.ordinalBadge} aria-hidden="true">
              {participant.ordinal}
            </span>
          )}
        </span>
        <span className={styles.legendText}>
          <span className={styles.legendName}>{participant.name}</span>
          <span className={styles.rowMeta}>
            {[participant.type, participant.side, participant.isKnown ? null : 'not introduced']
              .filter((part) => part !== null && part !== '')
              .join(' · ')}
          </span>
        </span>
      </button>

      <span className={styles.glyphRow}>
        <IconButton
          label={
            participant.isKnown
              ? `Take ${participant.name} back out of sight`
              : `Introduce ${participant.name}`
          }
          icon={participant.isKnown ? 'hide' : 'show'}
          disabled={busy || !live}
          onClick={() =>
            controls.updateParticipant.mutate({
              participantId: participant.participantId,
              isKnown: !participant.isKnown,
            })
          }
        />

        <IconButton
          label={
            participant.isDead
              ? `Put ${participant.name} back in the encounter`
              : `Mark ${participant.name} out of the encounter`
          }
          icon={participant.isDead ? 'refresh' : 'dead'}
          disabled={busy || !live}
          onClick={() =>
            controls.updateParticipant.mutate({
              participantId: participant.participantId,
              isDead: !participant.isDead,
            })
          }
        />

        {session.mapImageUrl !== null && !participant.isOnMap && (
          <IconButton
            // The label says the whole consequence for somebody nobody has met, because there is
            // one: putting a face on the shared map introduces them, and a Game Master who did not
            // expect that has just shown the table the thing in the alley.
            label={
              participant.isKnown
                ? `Put ${participant.name} on the map`
                : `Put ${participant.name} on the map, introducing them`
            }
            icon="add"
            disabled={busy || !live}
            onClick={() =>
              controls.addToken.mutate(
                { participantId: participant.participantId, x: 0.5, y: 0.5 },
                {
                  // Selected on arrival, so the very next thing — a drag, a hide — needs no
                  // second click to say who is meant.
                  onSuccess: onPlaced,
                },
              )
            }
          />
        )}

        {/* Another one of these.
            Only ever on a Temp — `canClone` is the server's word, so the rule about what a Temp
            is stays on one side of the wire. The new copy arrives numbered, unmet and alive,
            whatever this one is: a zombie that has just come round the corner has not been
            introduced because its twin was. */}
        {participant.canClone && (
          <IconButton
            label={`Another ${participant.name.replace(/ \d+$/, '')}`}
            icon="clone"
            disabled={busy || !live}
            onClick={() => controls.addParticipant.mutate({ characterId: participant.characterId })}
          />
        )}

        {/* And one fewer. Not offered on the last of them: the encounter still says that character
            is in this fight, so removing it here would be undone by the next sync — taking
            somebody out entirely is the Encounter tab. */}
        {participant.canClone && copies > 1 && (
          <IconButton
            label={`Take ${participant.name} out of the session`}
            icon="close"
            disabled={busy || !live}
            onClick={() =>
              controls.removeParticipant.mutate(
                { participantId: participant.participantId },
                { onSuccess: onRemoved },
              )
            }
          />
        )}
      </span>
    </div>
  )
}
